import { PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useState } from 'react'
import {
  createBankAccount,
  createWireBlotter,
  deleteBankAccount,
  deleteWireBlotter,
  getLoanSubledger,
  getTreasury,
  updateBankAccount,
  updateWireBlotter,
} from 'services'
import { svgLoading } from 'stories/assets'
import { confirm, formatDate, thousandSeperator } from 'utils'

import { EditBankAccountDialog } from './EditBankAccountDialog'
import { EditWireBlotterDialog } from './EditWireBlotterDialog'
import type { IBankAccount, IWireBlotterRow } from './types'

const formatAmount = (value?: number) => {
  if (!value && value !== 0) return '-'
  return `$${thousandSeperator(value)}`
}

export const TreasuryWires = () => {
  const [isLoading, setLoading] = useState(false)
  const [bankAccounts, setBankAccounts] = useState<IBankAccount[]>([])
  const [bankTotals, setBankTotals] = useState<Record<string, number>>({})
  const [wireRows, setWireRows] = useState<IWireBlotterRow[]>([])
  const [wireTotals, setWireTotals] = useState<Record<string, number>>({})
  const [loanOptions, setLoanOptions] = useState<Record<string, string>>({})
  const [editBank, setEditBank] = useState<Partial<IBankAccount> | null>(null)
  const [editWire, setEditWire] = useState<Partial<IWireBlotterRow> | null>(null)

  useEffect(() => {
    fetch()
  }, [])

  const fetch = async () => {
    setLoading(true)
    try {
      const [treasury, subledger] = await Promise.all([getTreasury(), getLoanSubledger()])
      setBankAccounts(treasury.bankAccounts?.results || [])
      setBankTotals(treasury.bankAccounts?.totals || {})
      setWireRows(treasury.wireBlotter?.results || [])
      setWireTotals(treasury.wireBlotter?.totals || {})
      setLoanOptions(
        (subledger.results || []).reduce((acc: Record<string, string>, row: { loanId: string }) => {
          if (row.loanId) acc[row.loanId] = row.loanId
          return acc
        }, {}),
      )
    } catch (error) {
      console.error(error)
      setBankAccounts([])
      setWireRows([])
    }
    setLoading(false)
  }

  const onSaveBank = async (data: Partial<IBankAccount>) => {
    if (editBank?.id) await updateBankAccount(editBank.id, data)
    else await createBankAccount(data)
    setEditBank(null)
    fetch()
  }

  const onSaveWire = async (data: Partial<IWireBlotterRow>) => {
    if (editWire?.id) await updateWireBlotter(editWire.id, data)
    else await createWireBlotter(data)
    setEditWire(null)
    fetch()
  }

  const onDeleteBank = async (item: IBankAccount) => {
    const ok = await confirm(`Delete bank account "${item.name}"?`)
    if (!ok) return
    setLoading(true)
    await deleteBankAccount(item.id)
    fetch()
  }

  const onDeleteWire = async (item: IWireBlotterRow) => {
    const ok = await confirm(`Delete wire to "${item.counterparty}"?`)
    if (!ok) return
    setLoading(true)
    await deleteWireBlotter(item.id)
    fetch()
  }

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-3 items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center">
          Treasury &amp; Wires
          {isLoading && (
            <span className="ml-3">
              <img src={svgLoading} className="inline w-6 h-6 animate-spin" />
            </span>
          )}
        </h1>
      </div>

      <p className="text-sm text-gray-600 mb-6">
        Bank balances pull from the Trial Balance (GL). The wire blotter is the operational record of cash movement
        behind the funding entries.
      </p>

      <div className="mb-8">
        <div className="flex flex-wrap gap-3 mb-3 items-center justify-between">
          <h2 className="text-lg font-semibold">Bank Accounts</h2>
          <button className="btn-icon flex items-center gap-1 text-sm" onClick={() => setEditBank({})}>
            <PlusIcon className="w-4 h-4" /> Add
          </button>
        </div>

        <div className="relative overflow-x-auto">
          <LayoutLoading show={isLoading} />
          <table className="w-full text-sm text-left text-gray-700">
            <thead className="text-xs uppercase bg-gray-100">
              <tr>
                {['Bank Account', 'GL Acct', 'Type', 'GL Balance', 'Action'].map((h) => (
                  <th key={h} className="px-3 py-2 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bankAccounts.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-gray-500">
                    No bank accounts yet.
                  </td>
                </tr>
              )}
              {bankAccounts.map((item) => (
                <tr key={item.id} className="border-b hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium">{item.name}</td>
                  <td className="px-3 py-2">{item.glAccountNo}</td>
                  <td className="px-3 py-2">{item.type}</td>
                  <td className="px-3 py-2 text-right">{formatAmount(item.glBalance)}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-2 items-center">
                      <span
                        className="text-shade-blue p-1 hover-shadow1 cursor-pointer"
                        onClick={() => setEditBank(item)}
                      >
                        <PencilSquareIcon className="w-4 h-4" />
                      </span>
                      <span className="text-red-800 p-1 hover-shadow1 cursor-pointer" onClick={() => onDeleteBank(item)}>
                        <TrashIcon className="w-4 h-4" />
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            {bankAccounts.length > 0 && (
              <tfoot>
                <tr className="bg-blue-50 font-semibold border-t-2">
                  <td colSpan={3} className="px-3 py-2">
                    Total cash
                  </td>
                  <td className="px-3 py-2 text-right">{formatAmount(bankTotals.glBalance)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div>
        <div className="flex flex-wrap gap-3 mb-3 items-center justify-between">
          <h2 className="text-lg font-semibold">Wire Blotter</h2>
          <button className="btn-icon flex items-center gap-1 text-sm" onClick={() => setEditWire({})}>
            <PlusIcon className="w-4 h-4" /> Add
          </button>
        </div>

        <div className="relative overflow-x-auto">
          <LayoutLoading show={isLoading} />
          <table className="w-full text-sm text-left text-gray-700 min-w-max">
            <thead className="text-xs uppercase bg-gray-100">
              <tr>
                {['Date', 'Direction', 'Counterparty', 'Loan', 'Amount', 'Purpose', 'Action'].map((h) => (
                  <th key={h} className="px-3 py-2 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {wireRows.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                    No wire records yet.
                  </td>
                </tr>
              )}
              {wireRows.map((item) => (
                <tr key={item.id} className="border-b hover:bg-gray-50">
                  <td className="px-3 py-2 whitespace-nowrap">
                    {item.wireDate ? formatDate(item.wireDate) : '-'}
                  </td>
                  <td className="px-3 py-2">{item.direction}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{item.counterparty || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{item.loanId || '-'}</td>
                  <td className="px-3 py-2 text-right">{formatAmount(item.amount)}</td>
                  <td className="px-3 py-2">{item.purpose || '-'}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-2 items-center">
                      <span
                        className="text-shade-blue p-1 hover-shadow1 cursor-pointer"
                        onClick={() => setEditWire(item)}
                      >
                        <PencilSquareIcon className="w-4 h-4" />
                      </span>
                      <span className="text-red-800 p-1 hover-shadow1 cursor-pointer" onClick={() => onDeleteWire(item)}>
                        <TrashIcon className="w-4 h-4" />
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            {wireRows.length > 0 && (
              <tfoot>
                <tr className="bg-blue-50 font-semibold border-t-2">
                  <td colSpan={4} className="px-3 py-2">
                    Net wires
                  </td>
                  <td className="px-3 py-2 text-right">{formatAmount(wireTotals.amount)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {editBank && <EditBankAccountDialog item={editBank} onClose={() => setEditBank(null)} onSave={onSaveBank} />}
      {editWire && (
        <EditWireBlotterDialog
          item={editWire}
          loanOptions={loanOptions}
          onClose={() => setEditWire(null)}
          onSave={onSaveWire}
        />
      )}
    </div>
  )
}
